/**
 * CostFormWizard.jsx
 * Formulario de Custo em Etapas Premium
 *
 * 3 Passos:
 * 1. Basico - Dispositivo, Nome, Categoria, Data, Preco, Descricao (obrigatorio)
 * 2. Atributos - Atributos customizados (opcional)
 * 3. Revisao
 */

import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  AttachMoney as CostIcon,
  Tune as TuneIcon,
  CheckCircle as ReviewIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';

import { useTranslation, useLocalization } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import openDatePicker from '../../common/util/openDatePicker';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import SelectField from '../../common/components/SelectField';
import EditAttributesAccordion from './EditAttributesAccordion';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();
  const { htmlLang } = useLocalization();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);
  const eventTimeRef = useRef(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SelectField
            fullWidth
            value={data.deviceId}
            emptyValue={null}
            onChange={(e) => updateData({ deviceId: e.target.value })}
            endpoint="/api/devices"
            label={t('sharedDevice')}
            className="premium-input"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <PremiumInput
            label={`${t('costCategory')} *`}
            value={data.type || ''}
            onChange={(e) => updateData({ type: e.target.value })}
            error={!!errors?.type}
            helperText={errors?.type}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            label={`${t('costDate')} *`}
            InputLabelProps={{ shrink: true }}
            value={data.eventTime ? moment(data.eventTime).locale('en').format(moment.HTML5_FMT.DATE) : ''}
            onChange={(e) => updateData({ eventTime: e.target.value })}
            error={!!errors?.eventTime}
            helperText={errors?.eventTime}
            InputProps={{
              inputProps: {
                type: 'date',
                lang: htmlLang,
                style: { colorScheme: 'dark' },
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" onClick={() => openDatePicker(eventTimeRef.current)}>
                    <CalendarIcon sx={{ color: colors.accent }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputRef={eventTimeRef}
            className="premium-input"
            sx={{
              '& input::-webkit-calendar-picker-indicator': {
                display: 'none',
                WebkitAppearance: 'none',
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <PremiumInput
            type="number"
            label={`${t('sharedPrice')} *`}
            value={data.price || '0'}
            onChange={(e) => updateData({ price: e.target.value })}
            error={!!errors?.price}
            helperText={errors?.price}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <PremiumInput
            label={t('sharedDescription')}
            value={data.description || ''}
            onChange={(e) => updateData({ description: e.target.value })}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 2: ATRIBUTOS
// ============================================================================

const AttributesStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: '10px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('wizardAttributesHelp')}
        </Typography>
      </Box>

      <EditAttributesAccordion
        attributes={data.attributes}
        setAttributes={(attributes) => updateData({ attributes })}
        definitions={{}}
      />
    </Box>
  );
};

// ============================================================================
// STEP 3: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const attributeCount = data.attributes ? Object.keys(data.attributes).length : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedName')} value={data.name} />
        <InfoRow label={t('costCategory')} value={data.type} />
        <InfoRow label={t('costDate')} value={data.eventTime} />
        <InfoRow label={t('sharedPrice')} value={data.price} />
        <InfoRow label={t('sharedAttributes')} value={attributeCount > 0 ? `${attributeCount} ${t('sharedAttributes').toLowerCase()}` : '-'} />
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CostFormWizard = ({ cost, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardCostBasicDescription') || 'Dados do custo',
      icon: <CostIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('sharedRequired');
        if (!data.type?.trim()) errors.type = t('sharedRequired');
        if (!data.eventTime) errors.eventTime = t('sharedRequired');
        if (!data.price) errors.price = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
        { label: t('sharedPrice'), value: data.price },
      ],
    },
    {
      label: t('sharedAttributes'),
      title: t('sharedAttributes'),
      description: t('wizardAttributesDescription'),
      icon: <TuneIcon sx={{ fontSize: 18 }} />,
      component: AttributesStep,
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = cost?.id ? `/api/costs/${cost.id}` : '/api/costs';
      const method = cost?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const saved = await response.json();
        onSave?.(saved);
        navigate('/settings/costs');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/costs');
  };

  const initialData = cost || {
    name: '',
    type: '',
    eventTime: '',
    price: '0',
    description: '',
    deviceId: null,
    attributes: {},
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={cost?.id ? null : 'cost-wizard-draft'}
      completeButtonText={cost?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default CostFormWizard;
